import {useQueryClient} from '@tanstack/react-query'
import {InputText} from 'primereact/inputtext'
import {useEffect, useRef, useState} from 'react'
import {useHistory} from 'react-router-dom'
import {SystemRole} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import {useUserHospital} from '../../context/hospital/UserHospitalContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyManager} from '../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {useAuth} from '../authentication/AuthProvider'
import {getClientId} from '../authentication/AuthUtils'
import FlexBox from '../layout/FlexBox'
import {
    getStaticListEventKey,
    onStaticListTableChangeRemoveListener,
    staticListEvent,
    staticListEventTypes,
} from './broadcast/StaticListEvent'
import {useListStaticList} from './query-keys/StaticListKeysManager'

const InitialFilterRequest = {}
const PatientByStaticList = () => {
    //#region Context
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    const {currentHospital} = useUserHospital()
    //#endregion

    //#region Translate
    const {translateCommons} = TranslateUtils()
    //#endregion

    //#region State
    const [filterValue, setFilterValue] = useState('')
    //#endregion

    //#region Ref
    const blockScrollRef = useRef()
    //#endregion

    //#region History
    const history = useHistory()
    //#endregion

    //#region Query
    const queryClient = useQueryClient()
    const {data: staticListOptions} = useListStaticList(
        user?.id,
        true,
        {
            ...InitialFilterRequest,
            hospitalId:
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : null,
            ownerId: user?.id,
        },
        !!user?.id && (userRole === SystemRole.ADMIN || !!currentHospital?.id),
    )
    //#endregion

    //#region Handle Function
    const handleMouseEnter = () => {
        blockOverflownScroll()
    }

    const handleMouseLeave = () => {
        unblockOverflownScroll()
    }
    //#endregion

    useEffect(() => {
        // TABLE CHANGE
        staticListEvent.on(
            `${getStaticListEventKey(staticListEventTypes.TABLE_CHANGE)}`,
            data => {
                if (
                    !data ||
                    !data?.id ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                if (
                    userRole === SystemRole.USER_WITH_ROLE &&
                    (!data?.hospitalId ||
                        data?.hospitalId !== currentHospital?.id)
                ) {
                    return
                }

                // Owner
                if (user?.id === data?.ownerId) {
                    return
                }

                const {staticListKeys} = QueryKeyManager(
                    QueryKeyTypes.staticList,
                    user?.id,
                )

                queryClient.invalidateQueries({
                    queryKey: staticListKeys.pattern(),
                })
            },
        )

        return () => {
            onStaticListTableChangeRemoveListener()
        }
    }, [])

    return (
        <div className="h-full w-full border-1 border-gray-200 border-round-lg overflow-y-auto">
            <div className="sticky w-full bg-white p-2">
                <div className=" flex flex-column gap-3 h-max justify-content-center align-items-center text-center col-12 bg-gray-50 border-round-top-lg">
                    <h4 className="m-0 p-0 font-bold overflow-dot overflow-text-1">
                        {translateCommons('Static list of patients')}
                    </h4>
                    <p className="text-sm pb-2">
                        {translateCommons(
                            'The curated list contains specific patient records for tracking and care purposes.',
                        )}
                    </p>
                </div>
                <hr className="m-0" />
                <div className=" col-12 px-0">
                    <span className="p-input-icon-right w-full">
                        <i className="pi pi-search" />
                        <InputText
                            className="h-3rem w-full"
                            placeholder={translateCommons('Search by name')}
                            value={filterValue || ''}
                            onChange={e => setFilterValue(e.target.value)}
                        />
                    </span>
                </div>
            </div>
            <div
                ref={blockScrollRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}>
                <ul className="p-2 m-0">
                    {staticListOptions && staticListOptions?.length > 0 ? (
                        staticListOptions.map(
                            item =>
                                item?.label
                                    ?.toLowerCase()
                                    .includes(filterValue?.toLowerCase()) && (
                                    <li
                                        key={item.value}
                                        className="flex align-items-center border-bottom-1 border-gray-100 mt-1 hover:bg-gray-50 cursor-pointer"
                                        onClick={() =>
                                            history.push(
                                                `/patients?staticListId=${item.value}`,
                                            )
                                        }>
                                        <div className="col-12">
                                            <FlexBox
                                                className={
                                                    'justify-content-between'
                                                }>
                                                <div className="col-10 m-0">
                                                    {item ? (
                                                        <p className="text-sm overflow-dot overflow-text-1 font-semibold">
                                                            {`${item.label} (${
                                                                item.extraData
                                                                    ? item.extraData
                                                                    : '0'
                                                            } ${translateCommons(
                                                                'results',
                                                            )})`}
                                                        </p>
                                                    ) : (
                                                        ''
                                                    )}
                                                    <p className="text-sm text-700 overflow-dot overflow-text-1">
                                                        {item.data
                                                            ? item.data
                                                            : translateCommons(
                                                                  'No description',
                                                              )}
                                                    </p>
                                                </div>
                                                <div>
                                                    <i
                                                        className="pi pi-list"
                                                        style={{
                                                            fontSize: '20px',
                                                        }}></i>
                                                </div>
                                            </FlexBox>
                                        </div>
                                    </li>
                                ),
                        )
                    ) : (
                        <div className="col-12">
                            <p className="text-center text-sm font-semibold text-600">
                                {translateCommons(
                                    'Currently, no static lists have been created.',
                                )}
                            </p>
                        </div>
                    )}
                </ul>
                <div className="p-virtualscroller-spacer"></div>
            </div>
        </div>
    )
}

export default PatientByStaticList
